import { MediaPlayerInstance, useMediaStore } from '@vidstack/react';
import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';

import { useLocalStorage, useSectionReviewModal } from '@/hooks';
import { useActivityTrack } from '@/store';

type IStore = {
  playerRef: { current: MediaPlayerInstance | null };
  setPlayerRef: (ref: React.RefObject<MediaPlayerInstance>) => void;
  closePlayerRef: () => void;
};

const PROGRESS_TO_SHOW_REVIEW = 0.95;
const PROGRESS_TO_BE_COMPLETED = 0.95;
const PROGRESS_TO_SHOW_NEXT_ACTIVITY_SHORTCUT = 0.99;

const store = create<IStore>((set, get) => ({
  playerRef: { current: null },
  setPlayerRef: (ref: React.RefObject<MediaPlayerInstance>) => {
    const { playerRef } = get();
    if (playerRef.current) return;
    set({ playerRef: { current: ref.current } });
  },
  closePlayerRef: () => {
    set({ playerRef: { current: null } });
  }
}));

export const useVideoResourcePlayer = () => {
  const { playerRef, setPlayerRef, closePlayerRef } = store();
  const {
    subscription,
    getActivityTrackById,
    getCurrentActivity,
    setIsRecapModalOpen,
    setActivityAsCompleted,
    getTimeTracking,
    showActivityReview,
    setShowActivityReview,
    setShowNextActivityShortcut,
    isRecapModalOpen,
    showNextActivityShortcut
  } = useActivityTrack();
  const { sectionIdToReview } = useSectionReviewModal();
  const { activity } = getCurrentActivity();
  const { isCompleted, review } = getActivityTrackById(activity?._id);
  const time = getTimeTracking(activity?._id);
  const { getNumber, save } = useLocalStorage();

  const mediaStore = useMediaStore(playerRef);
  const { started, playing, duration, currentTime } = mediaStore || {
    started: false,
    playing: false,
    duration: 0,
    currentTime: 0
  };

  const VIDEO_SEEN_KEY = useMemo(
    () => `VIDEO_SEEN_${subscription?._id}_${activity?._id}`,
    [subscription?._id, activity?._id]
  );

  const getVideoState = () => {
    return Boolean(getNumber(VIDEO_SEEN_KEY));
  };
  const [hasSeen, setHasSeen] = useState(getVideoState);
  const [completionTriggered, setCompletionTriggered] = useState(false);
  const [reviewModalTriggered, setReviewModalTriggered] = useState(false);

  useEffect(() => {
    setHasSeen(getVideoState());
    setCompletionTriggered(false);
    setReviewModalTriggered(false);
  }, [VIDEO_SEEN_KEY]);

  const handleStartVideo = () => {
    if (!playerRef.current) return;

    if (started && currentTime < duration && !isCompleted) {
      //TODO: event activity start video
    }
  };

  const handleProgress = () => {
    if (!playerRef.current || !duration) return;

    const progress = currentTime / duration;
    if (progress >= PROGRESS_TO_BE_COMPLETED && !hasSeen) {
      setHasSeen(true);
      save(VIDEO_SEEN_KEY, new Date().getUTCMilliseconds().toString());
    }

    if (
      progress >= PROGRESS_TO_SHOW_REVIEW &&
      playing &&
      !showActivityReview &&
      !review &&
      !reviewModalTriggered
    ) {
      setReviewModalTriggered(true);
      setShowActivityReview(true);
    }

    if (progress >= PROGRESS_TO_BE_COMPLETED && playing && !completionTriggered && !isCompleted) {
      if (activity && activity.questions && activity.questions.length > 0) {
        if (isRecapModalOpen) return;
        setCompletionTriggered(true);
        setIsRecapModalOpen(true);
        return;
      }

      setCompletionTriggered(true);
      setActivityAsCompleted();
    }

    if (
      progress >= PROGRESS_TO_SHOW_NEXT_ACTIVITY_SHORTCUT &&
      playing &&
      !showNextActivityShortcut &&
      !sectionIdToReview
    ) {
      setShowNextActivityShortcut(true);
    } else if (progress < PROGRESS_TO_SHOW_NEXT_ACTIVITY_SHORTCUT && showNextActivityShortcut) {
      setShowNextActivityShortcut(false);
    }
  };

  const handleSeekTo = (seconds: number) => {
    if (!playerRef.current) return;

    playerRef.current.remoteControl.seek(seconds);
  };

  const handleOnReady = () => handleSeekTo(time);

  return {
    currentTime,
    handleStartVideo,
    handleProgress,
    handleOnReady,
    playerRef,
    setPlayerRef,
    closePlayerRef,
    handleSeekTo
  };
};
